import { useState, useEffect, useCallback } from "react";
import NavigationButtons from "./NavigationButtons.jsx";
import QuoteEntry from "./QuoteEntry.jsx";
import { generateQuoteCheckEmail } from "../utils/emailGenerators.jsx";
import { useAutoSave } from "../hooks/useAutoSave.js";
import SavedStatusIndicator from "./SavedStatusIndicator.jsx";
import QuoteCheckPreview from "./QuoteCheckPreview.jsx";
import RestorePrompt from "./RestorePrompt.jsx";

const TOTAL_STEPS = 2;

const INITIAL_FORM_DATA = {
  qcStudentName: "",
  qcSourceName: "",
  quotes: [{ id: 1, text: "", context: "" }],
};

function validateStep(step, data) {
  switch (step) {
    case 1:
      if (!data.qcStudentName.trim()) return { field: "qcStudentName", message: "Please enter your name." };
      if (!data.qcSourceName.trim()) return { field: "qcSourceName", message: "Please enter the source's name." };
      break;
    case 2: {
      const validQuotes = data.quotes.filter((q) => q.text.trim());
      if (validQuotes.length === 0) {
        return { field: "quotes", message: "Please add at least one quote." };
      }
      break;
    }
    default:
      break;
  }
  return null;
}

function QuoteCheckForm({ onGenerateEmail, onStartOver, startFresh }) {
  const [currentStep, setCurrentStep] = useState(1);
  const [showPreview, setShowPreview] = useState(true);
  const [validationError, setValidationError] = useState(null);
  const [restoreDismissed, setRestoreDismissed] = useState(false);

  const {
    data: formData,
    setData: setFormData,
    clearSavedData,
    restoreSavedData,
    hasSavedData,
    isSaving,
    lastSaved,
    saveError,
  } = useAutoSave("pawPrint_quoteCheck_formData", INITIAL_FORM_DATA);

  const nextQuoteId = useCallback(() => {
    const maxId = formData.quotes.reduce((max, q) => Math.max(max, q.id || 0), 0);
    return maxId + 1;
  }, [formData.quotes]);

  const [nextId, setNextId] = useState(nextQuoteId());

  useEffect(() => {
    setNextId(nextQuoteId());
  }, [nextQuoteId]);

  useEffect(() => {
    if (startFresh) {
      clearSavedData();
      setCurrentStep(1);
      setValidationError(null);
      setRestoreDismissed(true);
      setNextId(2);
    }
  }, [startFresh, clearSavedData]);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));
    if (validationError?.field === id) {
      setValidationError(null);
    }
  };

  const handleQuoteChange = (id, newText) => {
    setFormData((prev) => ({
      ...prev,
      quotes: prev.quotes.map((quote) =>
        quote.id === id ? { ...quote, text: newText } : quote
      ),
    }));
    if (validationError?.field === "quotes") {
      setValidationError(null);
    }
  };

  const handleContextChange = (id, newContext) => {
    setFormData((prev) => ({
      ...prev,
      quotes: prev.quotes.map((quote) =>
        quote.id === id ? { ...quote, context: newContext } : quote
      ),
    }));
  };

  const handleAddQuote = () => {
    setFormData((prev) => ({
      ...prev,
      quotes: [...prev.quotes, { id: nextId, text: "", context: "" }],
    }));
    setNextId((prev) => prev + 1);
    if (validationError?.field === "quotes") {
      setValidationError(null);
    }
  };

  const handleRemoveQuote = (idToRemove) => {
    setFormData((prev) => ({
      ...prev,
      quotes: prev.quotes.filter((quote) => quote.id !== idToRemove),
    }));
  };

  const handleNext = () => {
    const error = validateStep(currentStep, formData);
    if (error) {
      setValidationError(error);
      return;
    }
    setValidationError(null);
    setCurrentStep((prev) => Math.min(prev + 1, TOTAL_STEPS));
  };

  const handlePrev = () => {
    setValidationError(null);
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleGenerate = () => {
    const error = validateStep(currentStep, formData);
    if (error) {
      setValidationError(error);
      return;
    }
    const { subject, body } = generateQuoteCheckEmail(formData);
    clearSavedData();
    setNextId(2);
    setRestoreDismissed(true);
    onGenerateEmail(subject, body);
  };

  const stepClass = (step) =>
    `form-step ${currentStep === step ? "active-step" : ""}`;

  return (
    <form
      id="quote-check-form"
      className="email-form"
      onSubmit={(e) => e.preventDefault()}
    >
      <div className="progress-indicator" id="quote-progress">
        <div className="step-dots">
          {Array.from({ length: TOTAL_STEPS }, (_, i) => (
            <div
              key={i}
              className={`step-dot ${
                i + 1 === currentStep
                  ? "active"
                  : i + 1 < currentStep
                  ? "completed"
                  : ""
              }`}
            />
          ))}
        </div>
        Step {currentStep} of {TOTAL_STEPS}
      </div>

      {hasSavedData && !restoreDismissed && (
        <RestorePrompt
          lastSaved={lastSaved}
          onResume={() => {
            restoreSavedData();
            setRestoreDismissed(true);
          }}
          onStartFresh={() => {
            clearSavedData();
            setRestoreDismissed(true);
          }}
        />
      )}

      <SavedStatusIndicator
        isSaving={isSaving}
        lastSaved={lastSaved}
        saveError={saveError}
        onClearSavedData={clearSavedData}
        formType="quote-check"
      />

      <h2>
        <i className="fas fa-check-double"></i> Draft Quote Check Email
      </h2>

      <button type="button" className="start-over-btn" onClick={onStartOver}>
        <i className="fas fa-redo"></i> Start Over
      </button>

      <div className={stepClass(1)} data-step="1">
        <h3>Step 1: Your &amp; Source&apos;s Info</h3>

        <label htmlFor="qcStudentName">Your Name:</label>
        <input
          type="text"
          id="qcStudentName"
          value={formData.qcStudentName}
          onChange={handleChange}
          aria-invalid={validationError?.field === "qcStudentName"}
        />

        <label htmlFor="qcSourceName">
          Source&apos;s Name (e.g., Mr. Smith, Noah Garcia):
        </label>
        <input
          type="text"
          id="qcSourceName"
          value={formData.qcSourceName}
          onChange={handleChange}
          aria-invalid={validationError?.field === "qcSourceName"}
        />
      </div>

      <div className={stepClass(2)} data-step="2">
        <h3>Step 2: Add Quotes &amp; Review</h3>

        <div id="quoteEntriesContainer">
          <p className="info-text">Add the quotes you want to check below.</p>

          {formData.quotes.map((quote, index) => (
            <QuoteEntry
              key={quote.id}
              id={quote.id}
              text={quote.text}
              context={quote.context}
              onQuoteChange={handleQuoteChange}
              onContextChange={handleContextChange}
              onRemove={handleRemoveQuote}
              showRemoveButton={formData.quotes.length > 1}
              index={index}
            />
          ))}
        </div>

        <button
          type="button"
          id="addQuoteEntryBtn"
          className="add-btn"
          onClick={handleAddQuote}
        >
          <i className="fas fa-plus"></i> Add Quote Entry
        </button>

        <p className="info-text" style={{ marginTop: "20px" }}>
          Ready to generate the email?
        </p>
      </div>

      {validationError && (
        <div className="validation-error" role="alert">
          <i className="fas fa-exclamation-circle"></i> {validationError.message}
        </div>
      )}

      <div className="toggle-switch-container">
        <label className="toggle-switch-label">
          <input
            type="checkbox"
            className="toggle-switch-input"
            checked={showPreview}
            onChange={() => setShowPreview((prev) => !prev)}
            role="switch"
            aria-label="Toggle email preview"
          />
          <span className="toggle-switch-slider"></span>
        </label>
        <span>Toggle Preview</span>
      </div>

      {showPreview && (
        <QuoteCheckPreview formData={formData} currentStep={currentStep} />
      )}

      <NavigationButtons
        currentStep={currentStep}
        totalSteps={TOTAL_STEPS}
        onPrev={handlePrev}
        onNext={handleNext}
        onGenerate={handleGenerate}
        formType="quote-check"
      />
    </form>
  );
}

export default QuoteCheckForm;
